<template>
  <b-modal
    v-model="modalShow"
    :title="config.modalTitle"
    size="lg"
    centered
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
  >
    <b-form @submit.prevent="submitHandler">
      <b-row>
        <b-col cols="12">
          <p class="text-muted mb-1">
            Updating {{ config.label }} for
            <strong>{{ selectedIds.length }}</strong> selected cheque(s).
          </p>
        </b-col>

        <!-- SUPPLIER MODE -->
        <b-col
          v-if="identifier === 'supplier'"
          cols="12"
        >
          <b-form-group label="Select New Supplier">
            <custom-v-select
              id="bulk-supplier"
              v-model="selectedSupplier"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="supplierOptions"
              label="text"
              placeholder="Select supplier..."
            />
            <p
              v-if="fieldErrors && fieldErrors.supplier"
              class="error-message"
            >
              {{ fieldErrors.supplier[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- COURIER MODE -->
        <template v-if="identifier === 'courier'">
          <b-col cols="6">
            <b-form-group
              label="Sent Through"
              label-for="sentThrough"
            >
              <custom-v-select
                id="sentThrough"
                v-model="sentThrough"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sentThroughOptions"
                label="text"
                @input="onSentThroughChange"
              />
              <p
                v-if="fieldErrors && fieldErrors.sent_through"
                class="error-message"
              >
                {{ fieldErrors.sent_through[0] }}
              </p>
            </b-form-group>
          </b-col>

          <!-- Courier related fields -->
          <template v-if="sentThrough && sentThrough.value === 'courier'">
            <b-col cols="6">
              <b-form-group
                label="Courier"
                label-for="courier"
              >
                <custom-v-select
                  id="courier"
                  v-model="selectedCourier"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="courierOptions"
                  label="text"
                />
                <p
                  v-if="fieldErrors && fieldErrors.courier"
                  class="error-message"
                >
                  {{ fieldErrors.courier[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <label for="courier-date">Courier Date</label>
              <b-form-datepicker
                id="courier-date"
                v-model="courierDate"
                menu-class="w-100"
                calendar-width="100%"
              />
              <p
                v-if="fieldErrors && fieldErrors.courier_date"
                class="error-message"
              >
                {{ fieldErrors.courier_date[0] }}
              </p>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="Delivery Boy"
                label-for="deliveryBoy"
              >
                <custom-v-select
                  id="deliveryBoy"
                  v-model="selectedDeliveryBoy"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="deliveryBoyOptions"
                  label="text"
                />
                <p
                  v-if="fieldErrors && fieldErrors.delivery_boy"
                  class="error-message"
                >
                  {{ fieldErrors.delivery_boy[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="Courier Slip"
                label-for="courierSlip"
              >
                <b-form-file
                  id="courierSlip"
                  v-model="courierSlip"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  placeholder="Choose courier slip file..."
                />
                <p
                  v-if="fieldErrors && fieldErrors.courier_slip"
                  class="error-message"
                >
                  {{ fieldErrors.courier_slip[0] }}
                </p>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Docket Number"
                label-for="docketNumber"
              >
                <b-form-input
                  id="docketNumber"
                  v-model="docketNumber"
                  type="text"
                  placeholder="Docket Number"
                />
                <p
                  v-if="errorMessage && errorMessage.docket_number"
                  class="error-message"
                >
                  {{ errorMessage.docket_number[0] }}
                </p>
              </b-form-group>
            </b-col>
          </template>

          <!-- MR related fields -->
          <template v-if="sentThrough && sentThrough.value === 'MR'">
            <b-col cols="6">
              <b-form-group
                label="MR Name"
                label-for="mr"
                :state="mrValidationState"
              >
                <b-form-input
                  id="mr"
                  v-model="mr"
                  type="text"
                  placeholder="Enter MR Name"
                  :state="mrValidationState"
                  @input="onMrNameInput"
                />
                <b-form-invalid-feedback>
                  {{ mrErrorMessage }}
                </b-form-invalid-feedback>
                <p
                  v-if="fieldErrors && fieldErrors.mr"
                  class="error-message"
                >
                  {{ fieldErrors.mr[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="MR Mobile No."
                label-for="mr_mobile"
                :state="mrMobileValidationState"
              >
                <b-form-input
                  id="mr_mobile"
                  v-model="mrMobile"
                  type="text"
                  placeholder="Enter 10 digit mobile number"
                  maxlength="10"
                  :state="mrMobileValidationState"
                  @input="onMrMobileInput"
                />
                <b-form-invalid-feedback>
                  {{ mrMobileErrorMessage }}
                </b-form-invalid-feedback>
                <p
                  v-if="fieldErrors && fieldErrors.mr_mobile"
                  class="error-message"
                >
                  {{ fieldErrors.mr_mobile[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <label for="mr-handover-date">MR Handover Date</label>
              <b-form-datepicker
                id="mr-handover-date"
                v-model="mrHandoverDate"
                menu-class="w-100"
                calendar-width="100%"
              />
              <p
                v-if="fieldErrors && fieldErrors.mr_handover_date"
                class="error-message"
              >
                {{ fieldErrors.mr_handover_date[0] }}
              </p>
            </b-col>
          </template>
        </template>

        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
            class="mt-1"
          >
            <div class="alert-body">
              <p>{{ errorMessage }}</p>
            </div>
          </b-alert>
        </b-col>
      </b-row>
    </b-form>

    <template #modal-footer="{ cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="submitting"
        @click="submitHandler"
      >
        {{ config.submitLabel }}
        <b-spinner
          v-if="submitting"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput, BFormDatepicker,
  BForm, BSpinner, BAlert, BFormFile, BFormInvalidFeedback,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CustomVSelect from '../UI/CustomVSelect.vue'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BFormDatepicker,
    BButton,
    BSpinner,
    BAlert,
    BFormFile,
    BFormInvalidFeedback,
  },
  props: {
    selectedIds: {
      type: Array,
      required: true,
    },
    identifier: {
      type: String,
      required: true,
      validator: value => ['supplier', 'courier'].includes(value),
    },
  },
  data() {
    return {
      modalShow: true,
      submitting: false,
      errorMessage: null,
      fieldErrors: {},
      showValidationErrors: false,

      // Supplier
      supplierOptions: [],
      selectedSupplier: null,

      // Courier related
      sentThrough: null,
      sentThroughOptions: [
        { value: 'courier', text: 'Courier' },
        { value: 'MR', text: 'MR' },
      ],
      selectedCourier: null,
      docketNumber: null,
      courierOptions: [],
      courierDate: null,
      selectedDeliveryBoy: null,
      deliveryBoyOptions: [],
      courierSlip: null,

      // MR
      mr: null,
      mrMobile: null,
      mrHandoverDate: null,
    }
  },
  computed: {
    config() {
      if (this.identifier === 'courier') {
        return {
          label: 'courier details',
          modalTitle: 'Bulk Update Courier',
          submitLabel: 'Update Courier',
        }
      }
      return {
        label: 'supplier',
        modalTitle: 'Bulk Update Supplier',
        submitLabel: 'Update Supplier',
      }
    },
    mrValidationState() {
      if (this.showValidationErrors && this.sentThrough && this.sentThrough.value === 'MR') {
        return !!(this.mr && this.mr.trim().length > 0)
      }
      return null
    },
    mrErrorMessage() {
      if (this.showValidationErrors && this.sentThrough && this.sentThrough.value === 'MR' && (!this.mr || this.mr.trim().length === 0)) {
        return 'MR Name is required'
      }
      return ''
    },
    mrMobileValidationState() {
      if (this.showValidationErrors && this.sentThrough && this.sentThrough.value === 'MR') {
        return !!(this.mrMobile && this.mrMobile.length === 10 && /^\d{10}$/.test(this.mrMobile))
      }
      return null
    },
    mrMobileErrorMessage() {
      if (this.showValidationErrors && this.sentThrough && this.sentThrough.value === 'MR') {
        if (!this.mrMobile || this.mrMobile.length === 0) {
          return 'Mobile number is required'
        }
        if (this.mrMobile.length !== 10 || !/^\d{10}$/.test(this.mrMobile)) {
          return 'Mobile number must be exactly 10 digits'
        }
      }
      return ''
    },
  },
  created() {
    this.fetchOptions()
  },
  methods: {
    fetchOptions() {
      if (this.identifier === 'supplier') {
        axios.get('/supplier_options')
          .then(res => {
            this.supplierOptions = res.data.data
          })
          .catch(() => {
            // Not blocking
          })
        return
      }

      // identifier === 'courier' — fetch both courier and delivery boy lists
      axios.get('/returns/courier_options')
        .then(res => {
          this.courierOptions = res.data.data
        })
        .catch(() => {})

      axios.get('/accounts/salesmans_options')
        .then(res => {
          this.deliveryBoyOptions = res.data.data
        })
        .catch(() => {})
    },

    onSentThroughChange() {
      // Reset all conditional fields when sent_through changes
      this.selectedCourier = null
      this.courierDate = null
      this.selectedDeliveryBoy = null
      this.courierSlip = null
      this.docketNumber = null
      this.mr = null
      this.mrMobile = null
      this.mrHandoverDate = null
      this.showValidationErrors = false
      this.fieldErrors = {}
    },

    onMrNameInput(value) {
      this.mr = value.toUpperCase()
    },

    onMrMobileInput(value) {
      const numericValue = value.replace(/\D/g, '')
      this.mrMobile = numericValue.slice(0, 10)
    },

    validate() {
      if (this.identifier === 'supplier') {
        if (!this.selectedSupplier) {
          this.errorMessage = 'Please select a supplier'
          return false
        }
        return true
      }

      // courier identifier
      if (!this.sentThrough) {
        this.errorMessage = 'Please select how the cheque was sent'
        return false
      }

      if (this.sentThrough.value === 'MR') {
        if (!this.mr || this.mr.trim().length === 0) {
          return false
        }
        if (!this.mrMobile || this.mrMobile.length !== 10 || !/^\d{10}$/.test(this.mrMobile)) {
          return false
        }
      }

      return true
    },

    buildPayload() {
      // Returns a plain object (sent as JSON for supplier, as FormData for courier when a file is present)
      if (this.identifier === 'supplier') {
        return { supplier: this.selectedSupplier.id }
      }

      // Courier identifier — always set sent_through, and null-out the unused branch
      const payload = {
        sent_through: this.sentThrough.value,
      }

      if (this.sentThrough.value === 'courier') {
        // Courier branch — populate courier fields, null-out MR fields
        payload.courier = this.selectedCourier ? this.selectedCourier.id : null
        payload.courier_date = this.courierDate || null
        payload.delivery_boy = this.selectedDeliveryBoy ? this.selectedDeliveryBoy.id : null
        payload.docket_number = this.docketNumber
        payload.mr = null
        payload.mr_mobile = null
        payload.mr_handover_date = null
      } else if (this.sentThrough.value === 'MR') {
        // MR branch — populate MR fields, null-out courier fields
        payload.mr = this.mr
        payload.mr_mobile = this.mrMobile
        payload.mr_handover_date = this.mrHandoverDate || null
        payload.courier = null
        payload.courier_date = null
        payload.delivery_boy = null
        payload.courier_slip = null
        payload.docketNumber = null
      }

      return payload
    },

    hasFileUpload() {
      return this.identifier === 'courier'
        && this.sentThrough
        && this.sentThrough.value === 'courier'
        && this.courierSlip instanceof File
    },

    makePatchRequest(id) {
      if (this.hasFileUpload()) {
        const formData = new FormData()
        const payload = this.buildPayload()
        Object.keys(payload).forEach(key => {
          const value = payload[key]
          formData.append(key, value === null ? '' : value)
        })
        formData.append('courier_slip', this.courierSlip)

        return axios.patch(`/accounts/vatika_cheques/${id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }

      return axios.patch(`/accounts/vatika_cheques/${id}/`, this.buildPayload())
    },

    submitHandler() {
      this.errorMessage = null
      this.fieldErrors = {}
      this.showValidationErrors = true

      if (!this.validate()) {
        return
      }

      this.submitting = true

      Promise.all(this.selectedIds.map(id => this.makePatchRequest(id)))
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `${this.config.submitLabel} Success`,
              text: `${this.config.label.charAt(0).toUpperCase() + this.config.label.slice(1)} updated for ${this.selectedIds.length} cheque(s)`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('updated')
          this.modalShow = false
        })
        .catch(error => {
          if (error?.response?.data && typeof error.response.data === 'object') {
            const responseData = error.response.data
            if (responseData.detail) {
              this.errorMessage = responseData.detail
            } else {
              this.fieldErrors = responseData
              this.errorMessage = 'Please fix the errors below'
            }
          } else {
            this.errorMessage = 'Failed to update. Please try again.'
          }
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
